import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { profileApi, RecentLoginEntry, timeAgo } from "@/lib/catalog-api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ProfileSettings() {
  const { user, refreshMe } = useAuth();
  return (
    <div data-testid="profile-settings" className="space-y-12">
      <Section title="Identity" subtitle="Your public name in audit logs and shared links.">
        <div className="text-sm space-y-1">
          <p className="font-medium">{user?.full_name}</p>
          <p className="text-muted-foreground">{user?.email}</p>
          {!user?.email_verified && (
            <p className="text-oxblood text-xs mt-2">Email not yet verified.</p>
          )}
        </div>
      </Section>
      <PasswordChangeForm />
      <EmailChangeForm onChanged={refreshMe} />
      <RecentLogins />
    </div>
  );
}

function Section({ title, subtitle, children }: {
  title: string; subtitle?: string; children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="font-display text-xl">{title}</h2>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function PasswordChangeForm() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("New passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await profileApi.changePassword({ current_password: cur, new_password: next });
      toast.success("Password updated.");
      setCur(""); setNext(""); setConfirm("");
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : "Could not update password.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Change password" subtitle="Minimum 8 characters with letters and numbers.">
      <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="password-form" noValidate>
        <div>
          <Field id="cur" label="Current password" type="password" value={cur} onChange={setCur} required />
          {error && (
            <p className="mt-2 text-sm text-oxblood" role="alert" data-testid="password-error">
              {error}
            </p>
          )}
        </div>
        <Field id="next" label="New password" type="password" value={next} onChange={setNext} required minLength={8} />
        <Field id="confirm" label="Confirm new password" type="password" value={confirm} onChange={setConfirm} required minLength={8} />
        <Button type="submit" disabled={busy}
          data-testid="password-submit"
          className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10">
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
    </Section>
  );
}

function EmailChangeForm({ onChanged }: { onChanged: () => void }) {
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await profileApi.requestEmailChange(email);
      setPending(r.pending_email);
      toast.success("Confirmation email sent.");
      setEmail("");
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not start email change.");
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    try {
      await profileApi.cancelEmailChange();
      setPending(null);
      onChanged();
      toast.success("Cancelled.");
    } catch {
      toast.error("Couldn't cancel.");
    }
  }

  return (
    <Section title="Change email" subtitle="Two-step. We send a confirmation link to the new address.">
      {pending ? (
        <div className="flex flex-wrap items-center gap-3 text-sm border border-border p-4">
          <span>Pending confirmation: <span className="font-mono">{pending}</span></span>
          <Button variant="outline" onClick={cancel}
            data-testid="email-cancel-pending"
            className="rounded-none border-foreground/20 hover:bg-bone h-9 ml-auto">Cancel</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="email-change-form">
          <Field id="new-email" label="New email" type="email" value={email} onChange={setEmail} required />
          <Button type="submit" disabled={busy}
            data-testid="email-change-submit"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10">
            {busy ? "Sending…" : "Send confirmation"}
          </Button>
        </form>
      )}
    </Section>
  );
}

function RecentLogins() {
  const [data, setData] = useState<{
    last_login_at?: string | null;
    recent_logins: RecentLoginEntry[];
  } | null>(null);

  useEffect(() => {
    profileApi.recentLogins()
      .then((r) => setData({ last_login_at: r.last_login_at, recent_logins: r.recent_logins }))
      .catch(() => setData({ recent_logins: [] }));
  }, []);

  return (
    <Section title="Recent sign-ins" subtitle="Last 5 sign-ins to this account.">
      {!data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : data.recent_logins.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No sign-ins recorded yet.</p>
      ) : (
        <ul className="border border-border divide-y divide-border bg-background"
          data-testid="recent-logins">
          {data.recent_logins.map((r, i) => (
            <li key={i} className="px-4 py-3 text-sm grid grid-cols-[1fr_auto] gap-3">
              <div className="min-w-0">
                <p className="font-medium">{timeAgo(r.at)}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {r.ip || "unknown ip"}{r.user_agent ? ` · ${r.user_agent}` : ""}
                </p>
              </div>
              <span className="text-xs text-muted-foreground tabular-nums">{new Date(r.at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Field({ id, label, type = "text", value, onChange, required, minLength }: {
  id: string; label: string; type?: string;
  value: string; onChange: (v: string) => void;
  required?: boolean; minLength?: number;
}) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</Label>
      <Input id={id} type={type} value={value} onChange={(e: any) => onChange(e.target.value)}
        data-testid={`field-${id}`}
        required={required} minLength={minLength}
        className="mt-1.5 h-10 rounded-none" />
    </div>
  );
}
